<script lang="ts">
import { X, Plus, Check } from 'lucide-vue-next';
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'UploadExternalPoliciesModalv2',
  components: { X, Plus, Check, AppSelect },
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { TransformWorksheetToPolicies } from '@/utilities/ExternalPolicyUploadTransform';
import { acceptContentTowerTypes } from '@/utilities/tower/Upload';
import { ExternalPolicy } from '@/types/PolicyCBO';
import { accountService } from '@/services/api';
import AppModal from '@/components/v2/AppModal.vue';
import { parentAccountService } from '@/services/api';
import { Account, ParentAccountCompany } from '@/types/Account';
import { FileSpreadsheet } from 'lucide-vue-next';
import { documentService } from '@/services/v2/DocumentService.js';

const props = defineProps<{
  accountID: string;
  tenantName?: string;
  isParent?: boolean;
}>();

const emit = defineEmits<{
  'needs-update': [];
}>();

enum State {
  Uploading,
  Validating,
}

const binaryData = ref<string>('');
const currentFile = ref<File>();
const filename = ref<string>('');
const currentErrors = ref<string[]>([]);
const currentPolicies = ref<ExternalPolicy[]>([]);
const isValidating = ref<boolean>(false);
const isUploading = ref<boolean>(false);
const uploadErrors = ref<string[]>([]);
const isValid = ref<boolean>(false);
const fileInput = ref<HTMLInputElement | null>(null);
const showModal = ref<boolean>(false);
const modalState = ref<State>(State.Uploading);

// Parent Account
const account = ref<Account>();
const parentCompanies = ref<ParentAccountCompany[]>([]);
const policiesToCorrect = ref<ExternalPolicy[]>([]);

const actionButtonLabel = computed(() => {
  if (!props.isParent) {
    return 'Upload';
  }

  return modalState.value === State.Validating ? 'Upload' : 'Validate';
});

const policyAccountsValid = computed(() => {
  return policiesToCorrect.value.every((policy) => policy.accountId);
});

const actionDisabled = computed(() => {
  if (!props.isParent) {
    return !(currentFile.value && isValid);
  }

  // if parent, enabled first when file is valid, then again when all policies have an account
  return !(currentFile.value && isValid && (modalState.value === State.Validating ? policyAccountsValid.value : true));
});

const description = computed(() => {
  if (modalState.value === State.Validating) {
    return policyAccountsValid.value
      ? ''
      : 'Client names were not found for the following policies. Please correct these names before uploading.';
  }

  return 'Upload policies external to CAC business. Please upload CAC-placed policies in Epic.';
});

function handleAction() {
  if (props.isParent && modalState.value === State.Uploading) {
    checkChildAccounts();
    modalState.value = State.Validating;
  } else {
    uploadPolicies();
  }
}

async function handleDownloadTemplate() {
  const target = await documentService.downloadPolicyTemplate();

  const anchorElement = document.createElement('a');
  anchorElement.href = target;
  anchorElement.download = 'external_policy_template';
  anchorElement.setAttribute('target', '_blank');
  anchorElement.click();
  anchorElement.remove();
}

function checkChildAccounts() {
  for (let policy of currentPolicies.value) {
    const company = parentCompanies.value.find(
      (company) => company.accountName?.trim().toLowerCase() === policy.accountName?.trim().toLowerCase(),
    );
    if (company) {
      policy.accountId = company.accountId;
    } else if (policy.accountName?.trim().toLowerCase() === account.value?.accountName?.trim().toLowerCase()) {
      // when uploading policy tied to the parent account
      policy.accountId = account.value.accountID;
    }
  }

  policiesToCorrect.value = currentPolicies.value.filter((policy) => !policy.accountId);
  currentPolicies.value = currentPolicies.value.filter((policy) => policy.accountId);
}

async function uploadPolicies() {
  if (!isValid.value) {
    return false;
  }

  isUploading.value = true;

  let validPolicy: boolean;
  let messages: string[];

  try {
    if (props.isParent) {
      const response = await parentAccountService.uploadExternalPolicies(props.accountID, [
        ...currentPolicies.value,
        ...policiesToCorrect.value,
      ]);
      validPolicy = response.validPolicy;
      messages = response.messages;
    } else {
      const response = await accountService.uploadPolicies(props.accountID, currentPolicies.value);
      validPolicy = response.validPolicy;
      messages = response.messages;
    }
  } catch (err) {
    console.error(err);
    validPolicy = false;
    messages = ['An error occurred while uploading policies'];
  }

  isUploading.value = false;
  isValid.value = validPolicy;
  uploadErrors.value = messages || [];
  if (!validPolicy) {
    return false;
  }

  emit('needs-update');
}

function readFile(event: Event) {
  if (!event || !event.target) {
    return;
  }
  const filesToRead = (event.target as HTMLInputElement).files;
  if (!filesToRead || filesToRead.length === 0) {
    return;
  }

  const file = filesToRead[0];
  const reader = new FileReader();
  reader.onload = () => {
    try {
      binaryData.value = reader.result as string;
      filename.value = file.name;
      currentFile.value = file;
      isValidating.value = true;
      setTimeout(() => {
        const { policies, errors } = TransformWorksheetToPolicies(binaryData.value);
        isValidating.value = false;
        isValid.value = errors.length === 0;
        currentErrors.value = errors;
        currentPolicies.value = policies;
      }, 200);
    } catch (err) {
      console.error(err);
    }
  };
  reader.readAsArrayBuffer(file);
}

function resetFile() {
  if (!isValidating.value && !isUploading.value) {
    binaryData.value = '';
    currentFile.value = undefined;
    filename.value = '';
    currentErrors.value = [];
    currentPolicies.value = [];
    isValid.value = false;
    uploadErrors.value = [];
    modalState.value = State.Uploading;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
}

onMounted(async () => {
  if (props.isParent) {
    account.value = await accountService.findById(props.accountID);
    parentCompanies.value = await parentAccountService.companies(props.accountID, { external: 'true' });
  }
});
</script>

<template>
  <div>
    <button class="tw:btn tw:btn-sm tw:btn-primary" @click="showModal = true" data-testid="external-policy-upload">
      Upload {{ tenantName ? 'Non-' + tenantName : 'External' }} Policies <Plus :size="18" />
    </button>
    <AppModal
      v-if="showModal"
      :show="showModal"
      :loading="isValidating || isUploading"
      :action-button-label="actionButtonLabel"
      modal-class="tw:w-fit tw:min-w-120 tw:max-w-200"
      :action-disabled="actionDisabled"
      :handle-cancel="
        () => {
          resetFile();
          showModal = false;
        }
      "
      :handle-action="() => handleAction()"
      size="xl"
      ref="appModal">
      <template v-slot:title>
        <h2>Upload {{ tenantName ? 'Non-' + tenantName : 'External' }} Policies</h2>
        <p class="tw:text-sm tw:text-gray-600 tw:italic">{{ description }}</p>
      </template>

      <template v-slot:content>
        <div v-if="modalState === State.Uploading" class="tw:flex tw:flex-col tw:gap-6 tw:pt-4">
          <button
            class="tw:flex tw:items-center tw:gap-1 tw:duration-[75ms] tw:hover:text-secondary"
            @click="handleDownloadTemplate">
            <FileSpreadsheet :size="18" class="tw:text-secondary" />
            <span class="tw:text-sm">Download Excel Template</span>
          </button>

          <div class="tw:relative tw:flex tw:w-full tw:flex-col">
            <input
              class="tw:file-input-bordered tw:file-input tw:w-full tw:border tw:border-solid tw:file-input-sm tw:file-input-secondary"
              :class="{ 'tw:border-error': currentErrors.length > 0 }"
              ref="fileInput"
              type="file"
              @change="readFile"
              :accept="acceptContentTowerTypes" />
            <X
              v-if="currentFile"
              @click="resetFile"
              class="tw:absolute tw:top-1 tw:right-1 tw:cursor-pointer tw:text-gray-300 tw:hover:text-gray-500" />

            <div class="policy-validation-errors tw:mt-1 tw:p-2" v-if="currentErrors.length > 0 && !isValidating">
              <span class="text-error">Cannot upload due to the following:</span>
              <ul>
                <li v-for="(error, index) of currentErrors" :key="index">{{ error }}</li>
              </ul>
            </div>

            <div class="policy-upload-errors tw:mt-1 tw:p-2" v-if="uploadErrors.length > 0 && !isValidating">
              <span class="text-error">Upload failed due to the following:</span>
              <ul>
                <li v-for="(error, index) of uploadErrors" :key="index">{{ error }}</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          v-else-if="modalState === State.Validating"
          class="tw:flex tw:max-h-[50vh] tw:flex-col tw:gap-6 tw:overflow-y-auto">
          <table
            v-if="policiesToCorrect && policiesToCorrect.length > 0"
            class="tw:table-pin-rows tw:table tw:table-xs">
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Description</th>
                <th>Carrier</th>
                <th>Policy Number</th>
                <th>Corrected Account Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="policy in policiesToCorrect" :key="policy.id">
                <td class="tw:max-w-32 tw:text-secondary">{{ policy.accountName }}</td>
                <td class="tw:max-w-56">{{ policy.description }}</td>
                <td>{{ policy.insurerGroup }}</td>
                <td class="tw:max-w-44">{{ policy.mainPolicyNumber }}</td>
                <td class="tw:min-w-40">
                  <AppSelect
                    v-model="policy.accountId"
                    :options="
                      parentCompanies.map((company) => ({ value: company.accountId, label: company.accountName || '' }))
                    "
                    class="tw:h-9 tw:w-full"
                    placeholder="Select account" />
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="tw:flex tw:items-center tw:gap-2 tw:py-4">
            <Check :size="16" class="tw:text-green-600" />
            <span>All Policies are valid, click upload to continue</span>
          </div>

          <div class="tw:p-2" v-if="uploadErrors.length > 0 && !isValidating">
            <span class="text-error">Upload failed:</span>
            <ul>
              <li v-for="(error, index) of uploadErrors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>
      </template>
    </AppModal>
  </div>
</template>
